/**
 * ChecklistEditModal.js
 *
 * Modal for editing checklist items with full audit trail.
 * Supports add, edit, remove - all logged to central audit.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Plus,
  Pencil,
  Trash2,
  Lock,
  Save,
  AlertTriangle,
  Info
} from 'lucide-react';

/**
 * Single editable item row
 * NOTE: All items are now editable. The 'required' flag only indicates
 * the item must be completed during execution, not that it can't be customized.
 * Only items with 'locked: true' cannot be edited (reserved for future use).
 */
const EditableItemRow = ({ item, onEdit, onRemove, isNew = false }) => {
  // Only truly locked items cannot be edited (e.g., regulatory minimums)
  // The 'required' flag just means "must complete during execution"
  const isLocked = item.locked === true;
  const isRequired = item.required || item.regulation;

  return (
    <div className={`
      flex items-center gap-3 py-3 px-4 rounded-lg
      ${isNew ? 'bg-green-50 border border-green-200' : 'bg-white border border-slate-200'}
    `}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {isLocked && <Lock className="h-4 w-4 text-amber-500" />}
          <span className="text-slate-800">{item.text}</span>
        </div>
        {item.regulation && (
          <span className="text-xs text-amber-600 mt-1">{item.regulation}</span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {!isLocked && (
          <>
            <button
              onClick={() => onEdit(item)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              onClick={() => onRemove(item)}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
        {isRequired && (
          <span className="text-xs text-amber-600 px-2 py-1 bg-amber-50 rounded">
            Required
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Add/Edit Item Form
 */
const ItemForm = ({ item, onSave, onCancel, categories }) => {
  const [text, setText] = useState(item?.text || '');
  const [category, setCategory] = useState(item?.category || '');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) {
      setError('Check text is required');
      return;
    }
    if (text.trim().length < 5) {
      setError('Check text must be at least 5 characters');
      return;
    }
    onSave({ ...item, text: text.trim(), category: category || null });
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-cyan-50 rounded-lg border border-cyan-200 space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Check Item Text *
        </label>
        <input
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); setError(''); }}
          placeholder="e.g., Verify emergency lighting functional"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg
            focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          autoFocus
        />
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Category (optional)
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg
            focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
        >
          <option value="">No specific category</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </select>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700"
        >
          {item?.id ? 'Update' : 'Add'} Item
        </button>
      </div>
    </form>
  );
};

/**
 * Remove Confirmation
 */
const RemoveConfirmation = ({ item, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-red-900">Remove this check item?</p>
          <p className="text-sm text-red-700 mt-1">"{item.text}"</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Reason for removal (optional but recommended)
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Superseded by new company policy"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg
            focus:ring-2 focus:ring-red-500 focus:border-red-500"
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(item, reason)}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
        >
          Remove Item
        </button>
      </div>
    </div>
  );
};

/**
 * Main Modal
 */
const ChecklistEditModal = ({
  isOpen,
  onClose,
  items = [],
  categories = [],
  categoryLabel,
  onAddItem,
  onEditItem,
  onRemoveItem
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [removingItem, setRemovingItem] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setShowAddForm(false);
      setEditingItem(null);
      setRemovingItem(null);
      setPendingChanges([]);
    }
  }, [isOpen]);

  const handleAdd = (newItem) => {
    const itemWithMeta = {
      ...newItem,
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: 'custom',
      addedAt: new Date().toISOString()
    };
    onAddItem(itemWithMeta);
    setPendingChanges([...pendingChanges, { action: 'add', item: itemWithMeta }]);
    setShowAddForm(false);
  };

  const handleEdit = (updatedItem) => {
    onEditItem(updatedItem);
    setPendingChanges([...pendingChanges, { action: 'edit', item: updatedItem }]);
    setEditingItem(null);
  };

  const handleRemove = (item, reason) => {
    onRemoveItem(item.id, reason);
    setPendingChanges([...pendingChanges, { action: 'remove', item, reason }]);
    setRemovingItem(null);
  };

  if (!isOpen) return null;

  // Split items: only truly locked items are non-editable
  // All other items (including 'required' ones) can be customized per vessel
  const lockedItems = items.filter(i => i.locked === true);
  const editableItems = items.filter(i => i.locked !== true);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                Edit {categoryLabel} Checklist
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Add, edit, or remove items. All changes are logged.
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Audit Notice */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Audit Trail Active</p>
                <p className="mt-0.5">
                  All changes are logged with your name and timestamp for SMS compliance.
                </p>
              </div>
            </div>

            {/* Locked Items (Read-Only) - only shown if any items are explicitly locked */}
            {lockedItems.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Locked Items ({lockedItems.length}) - Cannot be edited
                </h3>
                <div className="space-y-2 opacity-60">
                  {lockedItems.map(item => (
                    <EditableItemRow key={item.id} item={item} onEdit={() => {}} onRemove={() => {}} />
                  ))}
                </div>
              </div>
            )}

            {/* Editable Items */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-2">
                Editable Items ({editableItems.length})
              </h3>

              {editableItems.length > 0 ? (
                <div className="space-y-2">
                  {editableItems.map(item => (
                    <React.Fragment key={item.id}>
                      {editingItem?.id === item.id ? (
                        <ItemForm
                          item={editingItem}
                          onSave={handleEdit}
                          onCancel={() => setEditingItem(null)}
                          categories={categories}
                        />
                      ) : removingItem?.id === item.id ? (
                        <RemoveConfirmation
                          item={removingItem}
                          onConfirm={handleRemove}
                          onCancel={() => setRemovingItem(null)}
                        />
                      ) : (
                        <EditableItemRow
                          item={item}
                          onEdit={() => setEditingItem(item)}
                          onRemove={() => setRemovingItem(item)}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg">
                  No editable items yet. Add your first item below.
                </p>
              )}
            </div>

            {/* Add New Form */}
            {showAddForm ? (
              <ItemForm
                item={null}
                onSave={handleAdd}
                onCancel={() => setShowAddForm(false)}
                categories={categories}
              />
            ) : (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full py-3 px-4 border-2 border-dashed border-cyan-300 rounded-lg
                  text-cyan-600 hover:bg-cyan-50 hover:border-cyan-400 transition-colors
                  flex items-center justify-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Add New Check Item
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              {pendingChanges.length > 0 && (
                <span className="text-green-600 font-medium">
                  {pendingChanges.length} change(s) saved
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistEditModal;
